/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🔔 Notifications Store
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Account-scoped notifications, persisted in Firestore at
/// `accounts/{accountId}/notifications/{notificationId}`. Read state is
/// per-user via the `readBy` array on each doc; soft dismissal is
/// per-user via `dismissedBy`. New notifications stream in via
/// `onSnapshot` so any browser tab the operator has open updates
/// immediately when an admin posts an account message or a sync writer
/// produces a new cost-anomaly alert.
///
/// Sources today:
///   - cost_anomaly         — written by useNotificationSync from
///                            dashboards/overview after fetchAnomalies
///   - long_running_query   — same composable, after fetchHealth
///   - account_message      — written directly by admin tooling
///                            (any document an admin/CF posts to the
///                            notifications subcollection surfaces here)
///
/// Read state design choices:
///   - We keep readBy / dismissedBy as user-id arrays on the doc rather
///     than a per-user subcollection so the listener can compute
///     unreadCount without N+1 reads. Trade-off: doc size grows with
///     member count; fine for typical accounts but worth revisiting if
///     an account ever has 1k+ members.
///   - Sync writers use `setDoc(..., { merge: true })` and intentionally
///     don't include readBy/dismissedBy in their payload, so re-syncs
///     don't blow away the user's read state.

import { defineStore } from 'pinia'
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  arrayUnion,
  writeBatch,
  Timestamp,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore'

// ---------------------------------------------------------------------------
// Public schema. Surfaces use this directly — keeping the type alongside the
// store lets TS catch any drift between the store payload and the bell /
// toast / page consumers.
// ---------------------------------------------------------------------------

export type NotificationSeverity = 'info' | 'warning' | 'critical'
export type NotificationType =
  | 'cost_anomaly'
  | 'long_running_query'
  | 'account_message'

export interface NotificationLink {
  /** App route to navigate to on click. Null/undefined = no navigation,
   *  just mark-read. */
  path?: string
  /** Optional query string params (preserved as-is on the navigation). */
  query?: Record<string, string>
}

export interface NotificationSource {
  // cost_anomaly
  anomalyId?: string
  warehouse?: string
  /** YYYY-MM-DD slice of the anomaly day. Used to deep-link the
   *  AnomalyInvestigationDrawer back to the original (warehouse, day). */
  day?: string
  cost?: number
  expectedCost?: number
  // long_running_query
  queryId?: string
  userName?: string
  durationSec?: number
  // account_message — left open on purpose; admins can stash
  // arbitrary structured payload here for future surfaces.
  [key: string]: any
}

export interface AppNotification {
  id: string
  type: NotificationType
  severity: NotificationSeverity
  title: string
  body: string
  /** When the underlying event happened (anomaly day, query start, etc.).
   *  Sorted descending in the bell / page lists. */
  createdAt: Timestamp | null
  source?: NotificationSource
  link?: NotificationLink
  readBy: string[]
  dismissedBy: string[]
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface State {
  /** All non-dismissed notifications for the active user, newest first. */
  notifications: AppNotification[]
  /** Notification IDs the active user HAS dismissed. Populated alongside
   *  `notifications` from the same Firestore snapshot — these never
   *  appear in `notifications` but other surfaces (e.g. the cost
   *  anomalies card on /dashboards/overview) want to know "has this
   *  id been dismissed?" so they can hide a freshly-fetched row that
   *  the user already swiped away. */
  dismissedIds: Set<string>
  /** True once the listener has fired its first snapshot — used to
   *  avoid showing "no notifications" before Firestore lands. */
  hydrated: boolean
  /** Active user — captured from `bind()` so action methods don't have
   *  to re-read the globalsStore. */
  userId: string | null
  /** Active account — same reasoning. */
  accountId: string | null
  /** ID set we've already fired toasts for in this session. The toast
   *  container reads `toastQueue` and pops items off; this set
   *  remembers what's been emitted so we don't re-toast on snapshot
   *  refreshes that re-deliver the same items. */
  toastedIds: Set<string>
  /** New criticals waiting to be rendered as toasts. Append-only from
   *  the listener; the toast container shifts items off as it
   *  processes them. */
  toastQueue: AppNotification[]
}

let unsubscribe: Unsubscribe | null = null

export const useNotificationsStore = defineStore('notifications', {
  state: (): State => ({
    notifications: [],
    dismissedIds: new Set<string>(),
    hydrated: false,
    userId: null,
    accountId: null,
    toastedIds: new Set<string>(),
    toastQueue: [],
  }),

  getters: {
    /** Notifications visible to the current user — i.e. not dismissed
     *  by them. Already filtered out before they land in `notifications`,
     *  but kept as a getter so the bell can switch between
     *  "all visible" and "unread only" without store-level changes. */
    visible: (state): AppNotification[] => state.notifications,

    unread(state): AppNotification[] {
      const uid = state.userId
      if (!uid) return state.notifications
      return state.notifications.filter(n => !n.readBy.includes(uid))
    },

    unreadCount(): number {
      return this.unread.length
    },

    criticalCount(): number {
      return this.unread.filter(n => n.severity === 'critical').length
    },
  },

  actions: {
    /**
     * Start the per-account listener. Idempotent — calling bind() with
     * the same (accountId, userId) tuple is a no-op. Auth + signin
     * lifecycle should call this on login and `teardown()` on logout.
     */
    bind(accountId: string | null, userId: string | null) {
      if (!accountId || !userId) {
        this.teardown()
        return
      }
      // Already bound to this account/user pair — nothing to do.
      if (this.accountId === accountId && this.userId === userId && unsubscribe) return

      // Different account or user — tear down the old listener first.
      this.teardown()
      this.accountId = accountId
      this.userId = userId

      const { $firestore } = useNuxtApp()
      const db = $firestore as Firestore
      const col = collection(db, 'accounts', accountId, 'notifications')
      const q = query(col, orderBy('createdAt', 'desc'))

      unsubscribe = onSnapshot(
        q,
        (snap) => {
          const next: AppNotification[] = []
          // Rebuild the dismissed set from scratch each snapshot so
          // un-dismissals (a teammate / admin tooling removing the
          // user from `dismissedBy`) are reflected immediately.
          const dismissed = new Set<string>()
          for (const docSnap of snap.docs) {
            const data = docSnap.data() as Partial<AppNotification>
            // Track dismissed-by-this-user IDs even though we exclude
            // them from `notifications`. Other surfaces (e.g. the
            // cost-anomalies card on /dashboards/overview) cross-
            // reference this set to filter out rows whose persisted
            // notification has been dismissed.
            if ((data.dismissedBy || []).includes(userId)) {
              dismissed.add(docSnap.id)
              continue
            }
            const notification: AppNotification = {
              id: docSnap.id,
              type: (data.type as NotificationType) || 'account_message',
              severity: (data.severity as NotificationSeverity) || 'info',
              title: String(data.title || ''),
              body: String(data.body || ''),
              createdAt: (data.createdAt as Timestamp) ?? null,
              source: data.source,
              link: data.link,
              readBy: data.readBy || [],
              dismissedBy: data.dismissedBy || [],
            }
            next.push(notification)
          }
          // Detect newly arrived critical-severity items so the toast
          // container can surface them. We only enqueue items that
          // aren't already on the user's read list (no spamming on
          // login if they're already-acknowledged criticals from days
          // ago) and that we haven't toasted in this session.
          for (const n of next) {
            if (n.severity !== 'critical') continue
            if (n.readBy.includes(userId)) continue
            if (this.toastedIds.has(n.id)) continue
            this.toastedIds.add(n.id)
            this.toastQueue.push(n)
          }
          this.notifications = next
          this.dismissedIds = dismissed
          this.hydrated = true
        },
        (err) => {
          // Common cause: Firestore rules deny the listener (account
          // not yet provisioned, or user lacks access). We log instead
          // of throwing so the bell just shows empty rather than
          // blowing up the layout.
          console.warn('[notificationsStore] listener error:', err?.message || err)
        },
      )
    },

    /** Stop listening + reset state. Called on sign-out and from
     *  `bind()` when the active account/user changes. */
    teardown() {
      unsubscribe?.()
      unsubscribe = null
      this.notifications = []
      this.dismissedIds = new Set<string>()
      this.hydrated = false
      this.accountId = null
      this.userId = null
      this.toastedIds = new Set<string>()
      this.toastQueue = []
    },

    /** Pop the next pending toast off the queue. The toast container
     *  calls this in its render loop. */
    consumeToast(): AppNotification | null {
      return this.toastQueue.shift() ?? null
    },

    /** Mark a single notification as read for the current user. Writes
     *  via arrayUnion so concurrent reads from another tab don't clobber. */
    async markRead(id: string) {
      if (!this.accountId || !this.userId) return
      const { $firestore } = useNuxtApp()
      const ref = doc(
        $firestore as Firestore,
        'accounts', this.accountId,
        'notifications', id,
      )
      try {
        await setDoc(ref, { readBy: arrayUnion(this.userId) }, { merge: true })
      } catch (err) {
        console.warn('[notificationsStore] markRead failed:', err)
      }
    },

    /** Mark every currently-unread notification as read. Uses a
     *  WriteBatch so the operation is atomic + the listener fires once. */
    async markAllRead() {
      if (!this.accountId || !this.userId) return
      const ids = this.unread.map(n => n.id)
      if (!ids.length) return
      const { $firestore } = useNuxtApp()
      const db = $firestore as Firestore
      const batch = writeBatch(db)
      for (const id of ids) {
        const ref = doc(db, 'accounts', this.accountId, 'notifications', id)
        batch.set(ref, { readBy: arrayUnion(this.userId) }, { merge: true })
      }
      try {
        await batch.commit()
      } catch (err) {
        console.warn('[notificationsStore] markAllRead failed:', err)
      }
    },

    /** Per-user dismissal. The doc stays in Firestore (other members
     *  may still want it) but the current user no longer sees it. */
    async dismiss(id: string) {
      if (!this.accountId || !this.userId) return
      const { $firestore } = useNuxtApp()
      const ref = doc(
        $firestore as Firestore,
        'accounts', this.accountId,
        'notifications', id,
      )
      try {
        await setDoc(ref, { dismissedBy: arrayUnion(this.userId) }, { merge: true })
      } catch (err) {
        console.warn('[notificationsStore] dismiss failed:', err)
      }
    },
  },
})
